import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, HelpCircle, FileBarChart, TrendingUp, Upload, Calculator, Target, AlertCircle, Mail, BarChart3, Shield, Wallet } from 'lucide-react';
import tradeCompassLogo from '@/assets/tradecompass-logo.png';

const Help = () => {
  const navigate = useNavigate();

  const faqCategories = [
    {
      title: "Getting Started",
      icon: TrendingUp,
      color: "bg-primary/10 text-primary",
      faqs: [
        {
          question: "What is GainTracker?",
          answer: "GainTracker is a professional portfolio analysis platform that helps you track, analyze, and optimize your investment positions. It supports multiple brokers including Interactive Brokers (IBKR) and Revolut, and provides detailed insights into your portfolio performance, including profit targets, stop-loss recommendations, and capital recovery strategies."
        },
        {
          question: "How do I start using GainTracker?",
          answer: "Simply sign up with your email address and start importing your portfolio data. You can import positions via Excel files, IBKR Activity Statements, or Revolut statements. Once imported, GainTracker will automatically analyze your positions and provide actionable recommendations."
        },
        {
          question: "Is my data secure?",
          answer: "Yes, absolutely. All your data is stored securely with row-level security (RLS), meaning only you can access your own positions and orders. We use industry-standard encryption and authentication to protect your information."
        }
      ]
    },
    {
      title: "Importing Data",
      icon: Upload,
      color: "bg-accent/10 text-accent",
      faqs: [
        {
          question: "How do I import from IBKR?",
          answer: "Navigate to 'IBKR Full Analysis PF' from the dashboard. You can upload your IBKR Activity Statement CSV file which contains all your trades, dividends, cash transactions, and positions. The system will parse the file and display comprehensive analysis including realized P/L, dividend income, and portfolio overview."
        },
        {
          question: "What file formats are supported?",
          answer: "GainTracker supports Excel files (.xlsx, .xls) for general portfolio imports, and CSV files for IBKR Activity Statements and Revolut statements. Make sure your Excel file includes columns for ticker, quantity, buy price, and optionally currency and ISIN."
        },
        {
          question: "How do I import from Revolut?",
          answer: "Click on 'Revolut Import' from the dashboard and upload your Revolut investment statement CSV. The system will automatically parse your positions and integrate them with your portfolio analysis."
        },
        {
          question: "Can I manually add positions?",
          answer: "Yes! Click the 'Add Position' button on the main dashboard to manually enter individual stock positions. You'll need to provide the ticker symbol, quantity, and buy price at minimum."
        }
      ]
    },
    {
      title: "Understanding Analysis",
      icon: Calculator,
      color: "bg-success/10 text-success",
      faqs: [
        {
          question: "What do the alert colors mean?",
          answer: "GainTracker uses a tiered alert system based on your position gains:\n\n• Yellow (15%+ gain): Suggests recovering 20% of your initial investment\n• Orange (30%+ gain): Suggests recovering 40% of your initial investment\n• Red (45%+ gain): Suggests recovering 100% of your initial investment (selling 50% of shares)\n\nThese alerts help you systematically take profits and reduce risk in your portfolio."
        },
        {
          question: "How is 'Added Value' calculated?",
          answer: "Added Value represents the unrealized profit or loss in CHF (Swiss Francs) for each position. It's calculated by taking the difference between the current market value and your cost basis, converted to CHF using current exchange rates."
        },
        {
          question: "What is the Stock Scorecard?",
          answer: "The Stock Scorecard provides a quick overview of positions that have actionable recommendations. It shows positions at different gain thresholds (15%, 30%, 45%) and suggests the number of shares to sell to recover your initial investment gradually."
        },
        {
          question: "How does the Fibonacci Retracement work?",
          answer: "The Fibonacci Retracement calculator helps you identify potential support and resistance levels for any stock. Enter a high and low price, and the tool calculates the key Fibonacci levels (23.6%, 38.2%, 50%, 61.8%, 78.6%) that traders commonly use for technical analysis."
        }
      ]
    },
    {
      title: "IBKR Full Analysis",
      icon: FileBarChart,
      color: "bg-orange-500/10 text-orange-600",
      faqs: [
        {
          question: "What's included in the IBKR analysis?",
          answer: "The IBKR Full Analysis provides comprehensive parsing of your Activity Statement including:\n\n• Portfolio Overview with all open positions\n• Complete trade history with realized P/L\n• Dividend and withholding tax breakdown\n• Cash transactions (deposits, withdrawals, fees)\n• Financial summary with NAV and performance metrics\n• Date range filtering for all transaction types"
        },
        {
          question: "How do I export dividend data for taxes?",
          answer: "In the Dividends tab of IBKR Analysis, click the 'Export for Tax' button to download a CSV file containing all dividend information organized by ticker, with gross dividends, withholding taxes, and net amounts - perfect for tax reporting."
        },
        {
          question: "What is the Total Fees Summary?",
          answer: "The Total Fees Summary card in the Cash tab aggregates all fees across your account including trading commissions, account fees, and withholding taxes, giving you a complete picture of your total costs."
        }
      ]
    },
    {
      title: "SMA Surveillance & Ichimoku",
      icon: BarChart3,
      color: "bg-purple-500/10 text-purple-600",
      faqs: [
        {
          question: "What is SMA Surveillance?",
          answer: "SMA Surveillance monitors your positions against key Simple Moving Averages (20-day and 50-day). It helps identify when stocks are trading above or below these important technical levels, which can signal potential buy or sell opportunities."
        },
        {
          question: "What is Ichimoku analysis?",
          answer: "Ichimoku Cloud is a comprehensive technical analysis indicator that provides information about support/resistance levels, trend direction, momentum, and potential buy/sell signals. Our analysis includes the Tenkan-sen, Kijun-sen, Senkou Span A/B (the cloud), and trading signals."
        },
        {
          question: "How do I interpret the price line in charts?",
          answer: "The price line is shown in bold yellow for maximum visibility. When the price is above the Ichimoku cloud, it suggests a bullish trend; when below, it suggests bearish conditions. The cloud itself acts as dynamic support/resistance."
        }
      ]
    },
    {
      title: "DB Orders",
      icon: Target,
      color: "bg-blue-500/10 text-blue-600",
      faqs: [
        {
          question: "What is DB Order?",
          answer: "DB Order is a feature for creating and tracking trading orders. You can create buy or sell orders with specific parameters like limit prices, validity dates, and stock exchange codes. Each order generates a PDF document and is tracked in your order history."
        },
        {
          question: "How do order PDFs work?",
          answer: "When you submit an order, a PDF document is automatically generated containing all order details. This PDF is stored securely and can be downloaded at any time from the archived orders table."
        },
        {
          question: "What order types are supported?",
          answer: "GainTracker supports Normal (market) orders and Limit orders. For limit orders, you must specify the limit price at which you want the order to execute."
        }
      ]
    },
    {
      title: "Account & Security",
      icon: Shield,
      color: "bg-red-500/10 text-red-600",
      faqs: [
        {
          question: "How do I reset my data?",
          answer: "On the main dashboard, click the 'Reset Data' button to permanently delete all your imported positions. This action cannot be undone, so please be certain before confirming."
        },
        {
          question: "Can other users see my data?",
          answer: "No. All data in GainTracker is isolated by user through Row Level Security (RLS). Your positions, orders, and analysis are only visible to you when logged in with your account."
        },
        {
          question: "How do I sign out?",
          answer: "Click the 'Sign Out' button in the top-right corner of the dashboard to securely log out of your account."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      {/* Decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>
      
      <div className="relative container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-4 bg-primary/10 rounded-2xl mb-6">
            <HelpCircle className="h-12 w-12 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Help & FAQ
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to know about using GainTracker for your portfolio analysis
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <img 
              src={tradeCompassLogo} 
              alt="TradeCompass" 
              className="h-6 w-auto"
            />
            <span className="text-sm text-muted-foreground">
              Provided by TradeCompass
            </span>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { label: "Import Guide", icon: Upload, href: "#importing-data" },
            { label: "Analysis Help", icon: Calculator, href: "#understanding-analysis" },
            { label: "IBKR Guide", icon: FileBarChart, href: "#ibkr-full-analysis" },
            { label: "Contact", icon: Mail, href: "#contact" }
          ].map((link) => (
            <Card 
              key={link.label}
              className="cursor-pointer hover:shadow-lg hover:border-primary/30 transition-all duration-300 group"
              onClick={() => {
                const element = document.querySelector(link.href);
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <CardContent className="flex flex-col items-center justify-center p-6">
                <link.icon className="h-8 w-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
                <span className="font-medium text-foreground">{link.label}</span>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqCategories.map((category, categoryIndex) => (
            <Card 
              key={category.title} 
              id={category.title.toLowerCase().replace(/\s+/g, '-')}
              className="overflow-hidden shadow-lg border-0 bg-card/80 backdrop-blur-sm"
            >
              <CardHeader className="bg-gradient-to-r from-muted/50 to-muted/30 border-b">
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-xl ${category.color}`}>
                    <category.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{category.title}</CardTitle>
                    <CardDescription className="mt-1">
                      {category.faqs.length} questions answered
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Accordion type="single" collapsible className="w-full">
                  {category.faqs.map((faq, faqIndex) => (
                    <AccordionItem 
                      key={faqIndex} 
                      value={`${categoryIndex}-${faqIndex}`}
                      className="border-b last:border-b-0"
                    >
                      <AccordionTrigger className="px-6 py-4 hover:bg-muted/30 text-left">
                        <span className="font-medium text-foreground pr-4">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6 pt-2">
                        <div className="text-muted-foreground leading-relaxed whitespace-pre-line">
                          {faq.answer}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact Section */}
        <Card id="contact" className="mt-12 overflow-hidden shadow-lg border-0 bg-gradient-to-br from-primary/5 to-accent/5">
          <CardContent className="p-8 text-center">
            <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-xl mb-4">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Still have questions?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Can't find what you're looking for? Our support team is here to help you get the most out of GainTracker.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="gap-2">
                <Mail className="h-4 w-4" />
                Contact Support
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/')}>
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-muted-foreground">
          <div className="flex items-center justify-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span className="font-semibold text-foreground">GainTracker</span>
          </div>
          <p>Professional Portfolio Analysis Platform</p>
        </div>
      </div>
    </div>
  );
};

export default Help;
